import { DateTime } from 'luxon';
import { AssignmentFormData, AssignmentModalType, getDateRanges } from './helpers';
import { z, ZodError } from 'zod';
import { AssignmentStatus } from 'century-core/core-apis/assignment/types';
import { DATE_FORMAT } from 'century-core/core-utils/utils/utils';
import { extractDateAndTime } from 'century-core/core-utils/utils/date/date';

const AssignmentSchema = z.object({
  assignmentName: z.string().trim().min(1, 'missing-assignment-name').max(200, 'assignment-name-exceeds-limit'),
  instructions: z.string().trim().max(1000, 'assignment-instructions-exceeds-limit'),
});

export const validateAssignment = (
  values: AssignmentFormData,
  extendedDueDate?: boolean,
  modalType?: AssignmentModalType,
  status?: AssignmentStatus,
  editValues?: AssignmentFormData
) => {
  const errors: Record<string, string> = {};

  try {
    AssignmentSchema.parse(values);
  } catch (error) {
    if (error instanceof ZodError) {
      Object.assign(errors, error.formErrors.fieldErrors);
    }
  }

  Object.assign(errors, validateAssignmentDatesAndTimes(values, extendedDueDate, status));

  // Validate that disabled fields are not bypassed by the user and edited
  if (modalType === 'edit' && status && editValues) {
    Object.keys(values).forEach(field => {
      if (getDisabledStatus(status, field) && editValues[field] !== values[field]) {
        errors[field] = 'field-cannot-be-edited';
      }
    });
  }

  return errors;
};

export const validateAssignmentDatesAndTimes = (
  values: { startDate: string; startTime: string; dueDate: string; dueTime: string },
  extendedDueDate = false,
  status?: AssignmentStatus
) => {
  const errors: Record<string, string> = {};

  // Consolidated field pairs for date and time
  (
    [
      ['startDate', 'startTime'],
      ['dueDate', 'dueTime'],
    ] as const
  ).forEach(([dateField, timeField]) => {
    const dateValue = values[dateField];
    const timeValue = values[timeField];

    // If one of the pair is filled, both fields become required. ex. if start date is filled, start time should be filled as well
    // If date is empty then time should be disabled and ignored
    if (dateValue || timeValue) {
      if (dateValue && timeValue === '') errors[dateField] = 'missing-date-or-time';
    }
  });

  const startDateTime = DateTime.fromISO(`${values.startDate}T${values.startTime}`);
  const dueDateTime = DateTime.fromISO(`${values.dueDate}T${values.dueTime}`);

  // Check if start date and due date are the same or if due date is the day after start date
  if (startDateTime.hasSame(dueDateTime, 'day') || dueDateTime.hasSame(startDateTime.plus({ days: 1 }), 'day')) {
    const timeDifference = dueDateTime.diff(startDateTime, 'minutes').minutes;

    // Validate if due time is at least 30 minutes after start time and avoid negative values
    if (timeDifference < 0 || (timeDifference < 30 && timeDifference >= 0)) {
      errors.dueTime = 'assignment-time-difference-error';
    }
  }

  // Validate if start time is outside the current
  const today = DateTime.local().toFormat(DATE_FORMAT);
  const { time: currentTime } = extractDateAndTime(DateTime.local().toISO());
  if (values.startDate === today && values.startTime < currentTime && !getDisabledStatus(status, 'startTime')) {
    errors.startTime = 'start-time-error';
  }

  const { startDateRange, dueDateRange } = getDateRanges(values.startDate, extendedDueDate);

  // Validate if the date is within the range even if user modifies the min and max date in the browser
  if (!getDisabledStatus(status, 'startDate')) {
    validateDateRange(values.startDate, startDateRange, 'startDate', errors);
  }

  if (!getDisabledStatus(status, 'dueDate')) {
    validateDateRange(values.dueDate, dueDateRange, 'dueDate', errors);
  }

  return errors;
};

// Helper function for date range validation
const validateDateRange = (date: string, range: string[], fieldName: string, errors: Record<string, string>) => {
  const dateTime = DateTime.fromISO(date);
  const minDate = DateTime.fromISO(range[0]).toLocaleString(DateTime.DATE_SHORT);
  const maxDate = DateTime.fromISO(range[1]).toLocaleString(DateTime.DATE_SHORT);

  // We need to include the min and max date in the error message for Safari in iOS
  if (dateTime < DateTime.fromISO(range[0]) || dateTime > DateTime.fromISO(range[1])) {
    errors[fieldName] = `Date must be between ${minDate} and ${maxDate}`;
  }
};

export const getDisabledStatus = (status?: AssignmentStatus, field?: string): boolean => {
  switch (status) {
    case 'IN_PROGRESS':
      return ['startDate', 'startTime'].some(item => field?.includes(item));
    case 'ENDED':
      return !(field === 'assignmentName' || field === 'lateSubmission');
    case 'CLOSED':
      return field !== 'assignmentName';
    case 'ARCHIVED':
      return true;
    case 'DRAFT':
    case 'SCHEDULED':
    default:
      return false;
  }
};
